import './style.scss'

import React, { JSX } from 'react'

import ArrowIcon from '../../../../assets/images/icons/arrow-2.svg?react'

export const homepageFaqStructuredData = [
    {
        question: 'Who can use World Trans Air?',
        answer: [
            'World Trans Air is designed for freight forwarders, brokers, shippers, airlines',
            'and cargo aircraft operators working with full-aircraft cargo charters and empty legs.',
        ].join(' '),
    },
    {
        question: 'Why is the platform free right now?',
        answer: [
            'World Trans Air is in an active growth phase, so verified users currently receive',
            'full access free of charge. Planned pricing changes will be communicated in advance.',
        ].join(' '),
    },
    {
        question: 'How does the request process work?',
        answer: [
            'A customer submits a request to operators, operators respond inside the platform,',
            'and both parties coordinate rates, terms, flight information, permits and documents in one workspace.',
        ].join(' '),
    },
    {
        question: 'How are prices set?',
        answer: 'Operators determine their own prices and commercial terms. World Trans Air does not set rates or add a commission to an operator’s offer.',
    },
    {
        question: 'Do I need a long-term contract or subscription?',
        answer: 'No. Registration and platform use are currently free. Access is limited to verified companies, and new users complete a basic KYC form.',
    },
    {
        question: 'How are data and communications kept secure?',
        answer: 'Chats and file exchanges use secure protocols, and platform data is stored in line with modern information-security practices.',
    },
    {
        question: 'How is the platform evolving?',
        answer: 'World Trans Air is developing live and historical aircraft-map features and an AI booking assistant for cargo charter requests.',
    },
]

export const SectionAskedQuestion = (): JSX.Element => {
    const questions = [
        {
            question: 'Who can use World Trans Air?',
            answer: (
                <p>

                    The platform is designed for
                    {' '}
                    <span>freight forwarders, brokers, shippers, airlines and cargo aircraft operators</span>
                    {' '}
                    working with cargo charter flights.
                    {' '}
                    <br />
                    World Trans Air is a single online system for
                    {' '}
                    <span>full-aircraft charters</span>
                    {' '}
                    and
                    {' '}
                    <span>empty legs</span>
                    .
                </p>
            ),
        },
        {
            question: 'Why is the platform free right now?',
            answer: (
                <>
                    <p>
                        We are in an active growth phase, and our main goal is to let as many
                        {' '}
                        industry professionals as possible try the platform and help us improve it.
                        {' '}
                        <br />
                        That’s why we provide
                        {' '}
                        <span>full access to all users — free, with no limits.</span>
                    </p>
                    <p>
                        As the service develops, we plan to introduce
                        {' '}
                        <span>subscription-based pricing.</span>
                        {' '}
                        Any changes to pricing will be communicated to users in advance.
                    </p>
                </>
            ),
        },
        {
            question: 'How does the request process work?',
            answer: (
                <div>
                    <ul>
                        <li>
                            The customer creates an online request and sends it directly to operators.
                        </li>
                        <li>
                            Operators receive the request and respond with their offers inside the system. All communication takes place via the built-in chat.
                        </li>
                        <li>
                            All details — routes, rates, terms, comments — remain in a
                            {' '}
                            <span>single workspace.</span>
                        </li>
                        <li>
                            After a booking is confirmed, both parties can securely exchange flight information, permits and documents directly on the platform.
                        </li>
                    </ul>
                </div>
            ),
        },
        {
            question: 'How are prices set?',
            answer: (
                <>
                    <p>
                        <span>Prices and commercial terms are determined entirely by the operators.</span>
                    </p>
                    <div>
                        World Trans Air:
                        <ul>
                            <li>
                                does not influence pricing;
                            </li>
                            <li>
                                does not add commissions on top of operators’ rates.
                            </li>
                        </ul>
                    </div>
                </>
            ),
        },
        {
            question: 'Do I need a long-term contract or subscription?',
            answer: (
                <>
                    <p>
                        <span>No.</span>
                    </p>
                    <ul>
                        <li>
                            Registration and use of the platform are currently
                            {' '}
                            <span>free of charge.</span>
                        </li>
                        <li>
                            Access is provided only to
                            {' '}
                            <span>verified companies.</span>
                        </li>
                        <li>
                            To simplify verification, we ask new users to complete a basic
                            {' '}
                            <span>KYC (Know Your Customer)</span>
                            {' '}
                            form.
                        </li>
                    </ul>

                </>
            ),
        },
        {
            question: 'How are data and communications kept secure?',
            answer: (
                <>
                    <p>
                        We place strong emphasis on
                        {' '}
                        <span>confidentiality and data protection:</span>
                    </p>
                    <ul>
                        <li>
                            all chats and file exchanges use secure protocols;
                        </li>
                        <li>
                            data is stored in line with modern information security standards;
                        </li>
                        <li>
                            operators and customers can work on the platform knowing their commercial and operational information is protected.
                        </li>
                    </ul>

                </>
            ),
        },
        {
            question: 'How is the platform evolving?',
            answer: (
                <>
                    <p>
                        We are currently working on several new capabilities, including:
                    </p>
                    <ul>
                        <li>
                            an interactive
                            {' '}
                            <span>Map</span>
                            {' '}
                            with live, historical and analytical data on flights, aircraft, countries, airports and other parameters;
                        </li>
                        <li>
                            an
                            {' '}
                            <span>AI booking assistant</span>
                            {' '}
                            that will quickly suggest the most suitable and cost-effective options for each specific request.
                        </li>
                    </ul>
                </>
            ),
        },
    ]

    return (
        <div className="section-asked-questions">
            <div className="section-asked-questions__container">
                <div className="section-asked-questions__section-title">/ QUESTIONS</div>
                <div className="section-asked-questions__content">
                    <h2 className="section-asked-questions__title">Frequently Asked Questions</h2>
                    <div className="section-asked-questions__text">Everything you need to know about using World Trans Air</div>
                    <div className="section-asked-questions__list">
                        {questions.map((item) => (
                            <details className="faq" key={item.question}>
                                <summary className="faq__summary">
                                    {item.question}
                                    <ArrowIcon className="faq__arrow" />
                                </summary>
                                <div className="faq__content">
                                    {item.answer}
                                </div>
                            </details>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    )
}
